/**
 * 百度地图 Web 服务 + JSAPI GL 逆地理/联想。
 * Web 服务须直连 api.map.baidu.com；当前 AK 仅开通 JSAPI，Web 服务返回 240 时自动降级 JSAPI。
 */
import { publicData } from '@/utils/publicConfig'

const BAIDU_LBS_ORIGIN = 'https://api.map.baidu.com'
const JSONP_TIMEOUT_MS = 15000
const BAIDU_MAP_CALLBACK = '__baiduMapReadyCb'

/** 百度坐标系类型：3=GCJ02，5=BD09ll */
export const BAIDU_COORD_FROM_GCJ02 = 3
export const BAIDU_COORD_TO_BD09LL = 5
export const BAIDU_COORD_FROM_BD09LL = 5
export const BAIDU_COORD_TO_GCJ02 = 3

export interface BaiduAddressComponent {
  province?: string
  city?: string
  district?: string
  street?: string
  street_number?: string
  town?: string
}

export interface BaiduReverseGeocodeResult {
  status: number
  message?: string
  result?: {
    formatted_address?: string
    sematic_description?: string
    addressComponent?: BaiduAddressComponent
    location?: { lat: number; lng: number }
  }
}

export interface BaiduPlaceSuggestionItem {
  name?: string
  city?: string
  district?: string
  province?: string
  address?: string
  uid?: string
  location?: { lat: number; lng: number }
}

export interface BaiduPlaceSuggestionResult {
  status: number
  message?: string
  result?: BaiduPlaceSuggestionItem[]
}

export interface BaiduGeoconvResult {
  status: number
  result?: Array<{ x: number; y: number }>
}

let bmapGlLoadPromise: Promise<void> | null = null

function getAk(): string {
  return publicData.baiduMapAk || ''
}

function isBMapReady(): boolean {
  const BMap = window.BMap as any
  return Boolean(BMap && typeof BMap.Point === 'function' && typeof BMap.Map === 'function')
}

function waitForBMap(maxMs = 15000): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now()
    const tick = () => {
      if (isBMapReady()) {
        resolve()
        return
      }
      if (Date.now() - start > maxMs) {
        reject(new Error('百度地图 JSAPI 初始化超时'))
        return
      }
      window.setTimeout(tick, 50)
    }
    tick()
  })
}

/** 加载百度 JSAPI 3.0（BMap.Point；WebGL 版缺 BMap 会导致选点报错） */
export function ensureBMapLoaded(): Promise<void> {
  if (isBMapReady()) {
    return Promise.resolve()
  }
  if (bmapGlLoadPromise) {
    return bmapGlLoadPromise
  }

  const ak = getAk()
  if (!ak) {
    return Promise.reject(new Error('百度地图 AK 未配置（VITE_BAIDU_MAP_AK）'))
  }

  bmapGlLoadPromise = new Promise((resolve, reject) => {
    const w = window as unknown as Record<string, unknown>
    const finish = () => {
      waitForBMap()
        .then(resolve)
        .catch(reject)
    }

    if (w[BAIDU_MAP_CALLBACK]) {
      const prev = w[BAIDU_MAP_CALLBACK] as () => void
      w[BAIDU_MAP_CALLBACK] = () => {
        prev()
        finish()
      }
    } else {
      w[BAIDU_MAP_CALLBACK] = finish
    }

    document.querySelectorAll('script[data-baidu-map-gl]').forEach((node) => node.remove())

    const existing = document.querySelector('script[data-baidu-map-v3]')
    if (existing) {
      finish()
      return
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = `https://api.map.baidu.com/api?v=3.0&ak=${encodeURIComponent(ak)}&callback=${BAIDU_MAP_CALLBACK}`
    script.async = true
    script.setAttribute('data-baidu-map-v3', '1')
    script.onerror = () => {
      bmapGlLoadPromise = null
      reject(new Error('百度地图 JSAPI 脚本加载失败'))
    }
    document.head.appendChild(script)
  })

  return bmapGlLoadPromise
}

/** 兼容旧调用名，内部已统一为 JSAPI 3.0 */
export function ensureBMapGlLoaded(): Promise<void> {
  return ensureBMapLoaded()
}

/** 百度 Web 服务 JSONP 请求 */
export function baiduLbsJsonp<T = Record<string, unknown>>(pathAndQuery: string): Promise<T> {
  const path = pathAndQuery.startsWith('/') ? pathAndQuery : `/${pathAndQuery}`
  const sep = path.includes('?') ? '&' : '?'
  const cb = `baiduLbsCb_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`

  let done = false
  const w = window as unknown as Record<string, unknown>

  const dispose = () => {
    w[cb] = () => {}
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')

    const fail = (err: Error) => {
      if (done) return
      done = true
      dispose()
      clearTimeout(timer)
      try {
        script.remove()
      } catch {
        /* ignore */
      }
      reject(err)
    }

    const ok = (data: T) => {
      if (done) return
      done = true
      dispose()
      clearTimeout(timer)
      try {
        script.remove()
      } catch {
        /* ignore */
      }
      resolve(data)
    }

    w[cb] = (data: T) => {
      ok(data)
    }

    script.src = `${BAIDU_LBS_ORIGIN}${path}${sep}output=json&callback=${cb}`
    script.async = true
    script.onerror = () => fail(new Error('Baidu LBS JSONP failed'))

    const timer = window.setTimeout(() => {
      fail(new Error('Baidu LBS JSONP timeout'))
    }, JSONP_TIMEOUT_MS)

    document.head.appendChild(script)
  })
}

/** JSAPI Geocoder 逆地理（Web 服务不可用时降级） */
async function baiduReverseGeocodeByGl(
  lat: number,
  lng: number
): Promise<BaiduReverseGeocodeResult> {
  await ensureBMapLoaded()
  const BMap = window.BMap as any
  if (!BMap?.Geocoder || typeof BMap.Point !== 'function') {
    return { status: -1, message: 'BMap.Geocoder 不可用' }
  }

  return new Promise((resolve) => {
    const geoc = new BMap.Geocoder()
    const point = new BMap.Point(lng, lat)
    geoc.getLocation(point, (rs: any) => {
      if (!rs) {
        resolve({ status: -1, message: 'Geocoder 无结果' })
        return
      }
      const c = rs.addressComponents || {}
      resolve({
        status: 0,
        result: {
          formatted_address: rs.address || '',
          addressComponent: {
            province: c.province,
            city: c.city,
            district: c.district,
            street: c.street,
            street_number: c.streetNumber,
            town: c.town
          },
          location: { lat, lng }
        }
      })
    })
  })
}

/**
 * 逆地理编码（BD-09 坐标）：优先 Web 服务，失败则降级 JSAPI Geocoder
 */
export async function baiduReverseGeocode(
  lat: number,
  lng: number
): Promise<BaiduReverseGeocodeResult> {
  const ak = getAk()
  if (!ak) {
    throw new Error('百度地图 AK 未配置（VITE_BAIDU_MAP_AK）')
  }

  try {
    const q = [
      `ak=${encodeURIComponent(ak)}`,
      `coordtype=bd09ll`,
      `location=${lat},${lng}`,
      `extensions_poi=0`
    ].join('&')
    const res = await baiduLbsJsonp<BaiduReverseGeocodeResult>(`/reverse_geocoding/v3/?${q}`)
    if (res?.status === 0 && res.result) {
      return res
    }
  } catch {
    /* Web 服务不可用，降级 JSAPI */
  }

  return baiduReverseGeocodeByGl(lat, lng)
}

/** JSAPI LocalSearch 地点联想（Web 服务不可用时降级） */
async function baiduPlaceSuggestionByGl(
  query: string,
  region: string
): Promise<BaiduPlaceSuggestionResult> {
  await ensureBMapLoaded()
  const BMap = window.BMap as any
  if (!BMap?.LocalSearch) {
    return { status: -1, message: 'BMap.LocalSearch 不可用' }
  }

  return new Promise((resolve) => {
    const local = new BMap.LocalSearch(region, {
      onSearchComplete: (results: any) => {
        const status = typeof local.getStatus === 'function' ? local.getStatus() : 0
        // BMAP_STATUS_SUCCESS = 0
        if (status !== 0 || !results) {
          resolve({ status: status || -1, message: 'LocalSearch 无结果' })
          return
        }
        const items: BaiduPlaceSuggestionItem[] = []
        const num = typeof results.getCurrentNumPois === 'function' ? results.getCurrentNumPois() : 0
        for (let i = 0; i < num; i++) {
          const poi = results.getPoi(i)
          if (!poi) continue
          const pt = poi.point || poi.location
          items.push({
            uid: poi.uid,
            name: poi.title,
            address: poi.address,
            city: poi.city,
            province: poi.province,
            district: poi.district,
            location: pt ? { lng: pt.lng, lat: pt.lat } : undefined
          })
        }
        resolve({ status: 0, result: items })
      }
    })
    local.search(query)
  })
}

/**
 * 地点输入提示：优先 Web 服务，失败则降级 JSAPI LocalSearch
 */
export async function baiduPlaceSuggestion(
  query: string,
  region: string
): Promise<BaiduPlaceSuggestionResult> {
  const ak = getAk()
  if (!ak) {
    throw new Error('百度地图 AK 未配置（VITE_BAIDU_MAP_AK）')
  }

  try {
    const q = [
      `ak=${encodeURIComponent(ak)}`,
      `query=${encodeURIComponent(query)}`,
      `region=${encodeURIComponent(region)}`,
      'city_limit=true',
      'ret_coordtype=bd09ll'
    ].join('&')
    const res = await baiduLbsJsonp<BaiduPlaceSuggestionResult>(`/place/v2/suggestion?${q}`)
    if (res?.status === 0 && Array.isArray(res.result)) {
      return res
    }
  } catch {
    /* 降级 JSAPI */
  }

  return baiduPlaceSuggestionByGl(query, region)
}

/**
 * 坐标转换（单点）
 */
export async function baiduGeoconv(
  lng: number,
  lat: number,
  from = BAIDU_COORD_FROM_GCJ02,
  to = BAIDU_COORD_TO_BD09LL
): Promise<{ lng: number; lat: number } | null> {
  const ak = getAk()
  if (!ak) {
    throw new Error('百度地图 AK 未配置（VITE_BAIDU_MAP_AK）')
  }
  const q = [
    `ak=${encodeURIComponent(ak)}`,
    `coords=${lng},${lat}`,
    `from=${from}`,
    `to=${to}`
  ].join('&')
  const res = await baiduLbsJsonp<BaiduGeoconvResult>(`/geoconv/v1/?${q}`)
  if (res.status !== 0 || !res.result?.length) {
    return null
  }
  const point = res.result[0]
  return { lng: point.x, lat: point.y }
}

/** 从逆地理结果拼接可读地址 */
export function formatBaiduReverseAddress(result: BaiduReverseGeocodeResult['result']): string {
  if (!result) return ''
  if (result.formatted_address) {
    return result.formatted_address
  }
  const c = result.addressComponent
  if (!c) return ''
  return [c.province, c.city, c.district, c.town, c.street, c.street_number].filter(Boolean).join('')
}

